
import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Trash2, Eye, EyeOff } from 'lucide-react';
import { 
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { BenefitIcons, getIconComponent } from './BenefitIcons';
import { Benefit } from '@/lib/supabase/products';

interface BenefitItemProps {
  benefit: Benefit;
  updateBenefit: (id: string, field: keyof Benefit, value: any) => void;
  removeBenefit: (id: string) => void;
  toggleVisibility: (id: string) => void;
}

export const BenefitItem = ({ 
  benefit, 
  updateBenefit, 
  removeBenefit,
  toggleVisibility 
}: BenefitItemProps) => {
  return (
    <AccordionItem 
      key={benefit.id} 
      value={benefit.id} 
      className="border rounded-md dark:border-gray-700 dark:bg-[#1a1a2e] overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-2">
        <AccordionTrigger className="hover:bg-gray-50 dark:hover:bg-[#252A41] flex-1 py-2 transition-all">
          <div className="flex items-center space-x-2">
            <span className="text-gray-700 dark:text-gray-200">{getIconComponent(benefit.icon)}</span>
            <span className="text-gray-700 dark:text-gray-200">{benefit.title || "Nouveau bénéfice"}</span>
          </div>
        </AccordionTrigger>
        <div className="flex items-center gap-2 ml-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              toggleVisibility(benefit.id);
            }}
            className={benefit.visible === false ? "text-gray-400" : "text-green-600 dark:text-green-500"}
            title={benefit.visible === false ? "Bénéfice masqué" : "Bénéfice visible"}
          >
            {benefit.visible === false ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>
      <AccordionContent className="px-4 py-3 space-y-4 bg-[#151521]">
        <div className="space-y-2">
          <Label htmlFor={`title-${benefit.id}`} className="text-gray-700 dark:text-gray-300">Titre</Label>
          <Input
            id={`title-${benefit.id}`}
            value={benefit.title}
            onChange={(e) => updateBenefit(benefit.id, "title", e.target.value)}
            placeholder="Ex: Livraison rapide"
            className="bg-white dark:bg-[#151521] border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor={`description-${benefit.id}`} className="text-gray-700 dark:text-gray-300">Description</Label>
          <Input
            id={`description-${benefit.id}`}
            value={benefit.description}
            onChange={(e) => updateBenefit(benefit.id, "description", e.target.value)}
            placeholder="Ex: Recevez votre commande en 24h"
            className="bg-white dark:bg-[#151521] border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100"
          />
        </div>
        
        <div className="space-y-2">
          <Label className="text-gray-700 dark:text-gray-300">Icône</Label>
          <BenefitIcons 
            selectedIcon={benefit.icon} 
            onSelectIcon={(iconName) => updateBenefit(benefit.id, "icon", iconName)} 
          />
        </div>
        
        <div className="flex items-center space-x-2 pt-2">
          <Checkbox 
            id={`visible-${benefit.id}`}
            checked={benefit.visible !== false}
            onCheckedChange={(checked) => updateBenefit(benefit.id, "visible", checked)}
            className="text-blue-600 dark:text-blue-500"
          />
          <Label 
            htmlFor={`visible-${benefit.id}`} 
            className="cursor-pointer text-gray-700 dark:text-gray-300"
          >
            Afficher ce bénéfice
          </Label>
        </div>
        
        <div className="pt-2">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => removeBenefit(benefit.id)}
            className="w-full bg-red-600 hover:bg-red-700 text-white"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
